import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardActionArea, Grid, Stack, Typography } from "@mui/material";
import { Typography as FTypography, Stack as FStack } from "@financeable-com-au/financeable-ui";
import styled from "styled-components";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import Document from "./Document";
import CustomCardSending from "./CustomCardSending";
import {
  getApplicationDocumentPreview,
  saveValue,
  uploadDocument,
  userDetailsSelector,
} from "../../store/slices/applicationFormSlice";
import {
  ALLOWED_DOCUMENT_TYPE,
  ERRORS_MESSAGE,
  MAX_DOCUMENT_UPLOAD_SIZE,
  SUCCESS_MESSAGE,
  VALID_DOCUMENT_TYPE_EXTENSION,
  getIsCommercialOrBusinessApplication,
  getIsConsumerOrPersonalApplication,
  DOCUMENT_TEMPLATE_TYPE,
} from "../../constants";
import { RichTooltip } from "../CommonComponents/RichTooltip";
import { useTemplateServices } from "../../hooks";
import { openSnackbar } from "../../store/slices/snackbarSlice";

const CustomerPrivacyAgreement = React.memo(({ customer, applicationId, applicationType, isDisableEdit }) => {
  const dispatch = useDispatch();
  const { canUseConsumerPrivacyDocTemplate, canUseCommercialPrivacyDocTemplate } = useTemplateServices();
  const { customerPrivacyRequest, uploadPrivacyLoading, privacyLoading } = useSelector(userDetailsSelector);
  const isConsumerOrPersonalApplication = getIsConsumerOrPersonalApplication(applicationType);
  const isConsumerPrivacyDocTemplateMissing =
    !canUseConsumerPrivacyDocTemplate && isConsumerOrPersonalApplication;
  const isCommercialPrivacyDocTemplateMissing =
    !canUseCommercialPrivacyDocTemplate && getIsCommercialOrBusinessApplication(applicationType);

  const isPrivacyDisabled =
    !customer?.phoneNumbers?.[0]?.number ||
    !customer?.emails?.[0]?.address ||
    !customer?.firstName ||
    !customer?.lastName ||
    isCommercialPrivacyDocTemplateMissing ||
    isConsumerPrivacyDocTemplateMissing ||
    isDisableEdit;

  let documentTemplateText = null;
  if (isConsumerPrivacyDocTemplateMissing) {
    documentTemplateText = "Please add consumer privacy document template word file";
  } else if (isCommercialPrivacyDocTemplateMissing) {
    documentTemplateText = "Please add commercial privacy document template word file";
  }

  const fileInput = useRef();
  const [error, setError] = useState("");

  const Input = styled("input")({
    display: "none",
  });

  // Privacy doccuments.
  const privacyDocuments = customer?.documents?.filter((doc) => doc.name === "Privacy agreement") ?? [];
  // Show only the latest uploaded privacy document (falls back to insertion order when createdAt is missing).
  const latestPrivacyDocument = privacyDocuments.length
    ? privacyDocuments.reduce((latest, doc) =>
        new Date(doc.createdAt ?? 0) >= new Date(latest.createdAt ?? 0) ? doc : latest,
      )
    : null;
  // Validation from required fields.
  const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
  const privacyDocumentValidationMessage = getCustomerOrEntityLocalAndLenderError({
    localState: {},
    fieldName: `documents.[0].name_${customer._id}`,
  });
  const showPrivacyValidation = privacyDocuments.length === 0 && typeof privacyDocumentValidationMessage === "string";

  const handle = {
    uploadPrivacy: (event, customerId) => {
      if (isDisableEdit) return;
      const files = event.target.files;
      const newSelectedFiles = [];
      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (ALLOWED_DOCUMENT_TYPE.includes(file.type)) {
          if (file.size <= MAX_DOCUMENT_UPLOAD_SIZE) {
            newSelectedFiles.push(file);
          } else {
            setError(ERRORS_MESSAGE.documentSizeMsg);
          }
        } else {
          setError(ERRORS_MESSAGE.documentTypeMsg);
        }
      }
      if (newSelectedFiles?.length) {
        dispatch(saveValue({ uploadPrivacyLoading: true }));
        newSelectedFiles.forEach((file) => {
          formData.append("file", file);
        });
        formData.append("applicationId", applicationId);
        formData.append("customerId", customerId);
        formData.append("name", "Privacy agreement");

        dispatch(uploadDocument({ formData })).then((res) => {
          if (res?.payload?.data) {
            dispatch(
              openSnackbar({
                message: SUCCESS_MESSAGE.fileUploadSuccess,
                intent: "success",
              }),
            );
          } else {
            dispatch(
              openSnackbar({
                message: ERRORS_MESSAGE.fetchErrorMsg,
                intent: "danger",
              }),
            );
          }
        });
        event.target.input = null;
        setError("");
      }
    },
    generateDocumentPreview: () => {
      if (privacyLoading) return;
      const payload = {
        customerId: customer?._id,
        applicationId: applicationId,
        templateType: isConsumerOrPersonalApplication ? DOCUMENT_TEMPLATE_TYPE.CUSTOMER_PRIVACY_CONSUMER : DOCUMENT_TEMPLATE_TYPE.CUSTOMER_PRIVACY_COMMERCIAL,
      }
      // Manage loading separately
      dispatch(saveValue({ privacyLoading: true }));
      dispatch(getApplicationDocumentPreview(payload)).then((res) => {
        if (res.error) {
          dispatch(openSnackbar({
            message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
            intent: "danger",
          }));
        } else {
          // Open preview document modal
          dispatch(saveValue({
            privacyLoading: false,
            showPreviewDocument: {
              isOpen: true,
              customerId: payload?.customerId,
              templateType: payload?.templateType
            }
          }))
        }
      })
    }
  };

  return (
    <React.Fragment>
      <Grid
        item
        xs={12}
        style={{
          margin: "0 0 30px",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          paddingBottom: "30px",
          borderColor: showPrivacyValidation ? "red" : "rgba(0, 0, 0, 0.12)",
        }}
      >
        <FTypography mb={2.5} level="title-sm" textColor={showPrivacyValidation ? "danger.200" : undefined}>
          Privacy Agreement
        </FTypography>

        <Grid item xs={12} sx={{ minWidth: 0, marginBottom: "20px" }}>
          <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
            {latestPrivacyDocument && (
              <Document document={latestPrivacyDocument} customer={customer} name="privacyAgreement" />
            )}
          </Stack>
        </Grid>

        <Grid container item spacing={2}>
          <Grid item xs={12} sm={6}>
            <RichTooltip>
              <Card
                onClick={() => {
                  if (isPrivacyDisabled ? true : false) return;
                  handle.generateDocumentPreview();
                }}
              >
                <CardActionArea disableRipple={isPrivacyDisabled ? true : false}>
                  <CustomCardSending
                    title={"Get signed Privacy"}
                    description={`Send a self service link to digitally sign the privacy and consent statement.`}
                    imagePath={""}
                    loadingState={privacyLoading}
                    financialState={true}
                    icon={false}
                    disabled={isPrivacyDisabled ? true : false}
                    lastTitle={"Getting preview document..."}
                  />
                </CardActionArea>
              </Card>

              {isPrivacyDisabled && (
                <Typography variant="caption">
                  {documentTemplateText
                    ? documentTemplateText
                    : `Sending a privacy link requires a customer ${!customer?.firstName ? "first name, " : ""} ${!customer?.lastName ? "last name, " : ""} ${!customer?.emails?.[0]?.address ? "email, " : ""} ${!customer?.phoneNumbers?.[0]?.number ? "mobile number" : ""}`}
                </Typography>
              )}
              {customerPrivacyRequest && <Typography variant="caption">{SUCCESS_MESSAGE.privacy}</Typography>}
            </RichTooltip>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card>
              <Input
                accept={VALID_DOCUMENT_TYPE_EXTENSION}
                ref={fileInput}
                id={`upload-id-${customer._id}`}
                multiple
                type="file"
                value={null}
                disabled={isDisableEdit}
                onChange={(event) => handle.uploadPrivacy(event, customer._id)}
              />
              <CardActionArea onClick={() => fileInput.current.click()}>
                <CustomCardSending
                  title={"Upload sign privacy"}
                  description={`Upload a signed privacy statement from files stored locally on
              your computer.`}
                  imagePath={""}
                  loadingState={uploadPrivacyLoading}
                  financialState={true}
                  lastTitle={"Upload privacy..."}
                  disabled={isDisableEdit}
                />
              </CardActionArea>
            </Card>
            {error && (
              <Typography variant="body2" style={{ color: "red" }}>
                {error}
              </Typography>
            )}
          </Grid>
        </Grid>
        {showPrivacyValidation && (
          <FStack mt={5}>
            <FTypography level="body-xs" textColor="danger.200">
              {privacyDocumentValidationMessage}
            </FTypography>
          </FStack>
        )}
      </Grid>
    </React.Fragment>
  );
});

export default CustomerPrivacyAgreement;
